import { useEffect, useRef, useState } from 'react'
import type { ReplaySource } from './TaskEventReplayImpl'
import RowReplayPreviewImpl from './RowReplayPreviewImpl'
import './RowReplayPreview.css'

type ThumbnailReplaySource = Exclude<ReplaySource, { recordingId: string }>

// This preview is built from a handful of server-stored thumbnail JPEGs (see RowReplayPreviewImpl) —
// it does NOT pull in rrweb, so it is light enough to ship in the main bundle. It used to be
// code-split behind a lazy() dynamic import (back when the row preview embedded the heavy rrweb
// player), but after the rewrite to thumbnail frames that separate chunk bought no weight saving and
// only created a failure mode: a list can render many of these rows, and once a deploy replaced the
// hashed chunk, every tab still holding the old index.html fetched a 404 that the SPA redirect serves
// as index.html, so the import threw "Failed to fetch dynamically imported module" and the row was
// stuck showing "Preview unavailable" forever. Importing the impl statically removes that chunk
// entirely, so the preview always loads with the page. (The genuinely heavy, rrweb-based players —
// TaskEventReplay keeps its lazy+ChunkErrorBoundary treatment.)
//
// We still defer *mounting* the impl until the row scrolls into view: that gates the thumbnail
// network requests, so an off-screen row in a long list costs nothing until it is seen.

export type { ReplaySource } from './TaskEventReplayImpl'

export default function RowReplayPreview({ source, width, height, fit, live }: { source: ThumbnailReplaySource; width?: number; height?: number; fit?: 'contain' | 'cover'; live?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  const [seen, setSeen] = useState(false)
  // When a caller overrides the thumbnail box size, the skeleton placeholder must match so the
  // layout doesn't jump when the real preview mounts.
  const sizeStyle = width != null && height != null ? { width, height } : undefined

  useEffect(() => {
    if (seen) return
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some(e => e.isIntersecting)) {
          setSeen(true)
          io.disconnect()
        }
      },
      { rootMargin: '150px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [seen])

  return (
    <div ref={ref} className="row-preview" data-testid="row-preview">
      {seen ? (
        <RowReplayPreviewImpl source={source} width={width} height={height} fit={fit} live={live} />
      ) : (
        <div className="row-preview__skeleton" style={sizeStyle} aria-hidden />
      )}
    </div>
  )
}
